import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { AlertTriangle, Eye, EyeOff, Info, Plus, Trash2, Wrench, Zap } from 'lucide-react';
import {
  CONDITION_OPERATORS,
  FIELD_TYPES,
  TYPES_WITH_OPTIONS,
  TYPES_WITH_TRIGGERS,
  fieldValueChoices,
  finalizeField,
  hasFieldChanged,
  hasOtherOption,
  isDuplicateFieldName,
  needsCustomField,
  normalizeFieldType,
  parseOptionList,
  validateField,
  type AutomationTrigger,
  type CascadingOption,
  type ConditionOperator,
  type FormField,
} from '../utils/requestTypeFields';

interface RequestTypeFieldEditorProps {
  open: boolean;
  field: FormField | null;
  /** Every field on the form, the one being edited included. */
  allFields: FormField[];
  onClose: () => void;
  onSave: (field: FormField) => void;
}

const TYPE_GROUPS = ['Text', 'Choice', 'People', 'Date and time', 'Other'] as const;

function joinForEdit(values: string[] | undefined): string {
  return (values || []).join('\n');
}

/**
 * The field editor for one request-form field: everything the old FieldDialog and
 * FieldConfigurationWizard could set, in one dialog (RESTORE_PLAN R1.1).
 */
export function RequestTypeFieldEditor({ open, field, allFields, onClose, onSave }: RequestTypeFieldEditorProps) {
  const [draft, setDraft] = useState<FormField | null>(null);
  const [optionsRaw, setOptionsRaw] = useState('');
  const [parentsRaw, setParentsRaw] = useState('');
  const [childrenRaw, setChildrenRaw] = useState<Record<string, string>>({});
  const [showCondition, setShowCondition] = useState(false);
  const [triedToSave, setTriedToSave] = useState(false);

  const isNew = !!field && !allFields.some((f) => f.id === field.id);

  useEffect(() => {
    if (!open || !field) return;
    const start: FormField = { ...field, type: normalizeFieldType(field.type) };
    setDraft(start);
    setOptionsRaw(start.optionsRawInput || joinForEdit(start.options));
    setParentsRaw(joinForEdit((start.cascadingOptions || []).map((c) => c.parentValue)));
    const children: Record<string, string> = {};
    for (const c of start.cascadingOptions || []) children[c.parentValue] = c.childValues.join(', ');
    setChildrenRaw(children);
    setShowCondition(!!start.conditionalParent);
    setTriedToSave(false);
  }, [open, field]);

  const otherFields = useMemo(() => allFields.filter((f) => f.id !== field?.id && f.name), [allFields, field?.id]);
  const parentField = useMemo(() => otherFields.find((f) => f.id === draft?.conditionalParent), [otherFields, draft?.conditionalParent]);
  const parentChoices = fieldValueChoices(parentField);

  if (!draft) return null;

  const type = draft.type;
  const withOptions = TYPES_WITH_OPTIONS.includes(type);
  const isCascading = type === 'cascading';
  const withTriggers = TYPES_WITH_TRIGGERS.includes(type);
  const isAsset = type === 'asset';
  const update = (patch: Partial<FormField>) => setDraft((d) => (d ? { ...d, ...patch } : d));

  const duplicate = isDuplicateFieldName(draft.name, draft.id, allFields);
  const errors = validateField(draft, allFields);
  const changed = isNew || !field || hasFieldChanged(field, draft);
  const canSave = !!draft.name.trim() && !duplicate && changed;
  const customField = needsCustomField(draft);
  const triggerChoices = fieldValueChoices(draft);

  const applyOptions = (raw: string) => {
    setOptionsRaw(raw);
    update({ options: parseOptionList(raw), optionsRawInput: raw });
  };

  const rebuildCascading = (parents: string[], children: Record<string, string>) => {
    const next: CascadingOption[] = parents.map((p) => ({ parentValue: p, childValues: parseOptionList(children[p] || '') }));
    update({ cascadingOptions: next, options: parents });
  };

  const applyParents = (raw: string) => {
    setParentsRaw(raw);
    const parents = parseOptionList(raw);
    const children = { ...childrenRaw };
    for (const p of parents) if (!(p in children)) children[p] = '';
    setChildrenRaw(children);
    rebuildCascading(parents, children);
  };

  const applyChildren = (parent: string, raw: string) => {
    const children = { ...childrenRaw, [parent]: raw };
    setChildrenRaw(children);
    rebuildCascading(parseOptionList(parentsRaw), children);
  };

  const setCondition = (on: boolean) => {
    setShowCondition(on);
    if (!on) update({ conditionalParent: undefined, conditionalOperator: undefined, conditionalValue: undefined });
    else update({ conditionalOperator: draft.conditionalOperator || 'equals' });
  };

  const triggers = draft.automationTriggers || [];
  const setTriggers = (next: AutomationTrigger[]) => update({ automationTriggers: next });
  const addTrigger = () => setTriggers([...triggers, { id: `trigger_${Date.now()}_${triggers.length}`, triggerValue: '', automationDescription: '' }]);
  const updateTrigger = (id: string, patch: Partial<AutomationTrigger>) => setTriggers(triggers.map((t) => (t.id === id ? { ...t, ...patch } : t)));
  const removeTrigger = (id: string) => setTriggers(triggers.filter((t) => t.id !== id));

  const handleSave = () => {
    setTriedToSave(true);
    if (!canSave || errors.length > 0) return;
    onSave(finalizeField(draft));
  };

  const parents = parseOptionList(parentsRaw);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="request-type-field-editor">
        <DialogHeader>
          <DialogTitle>{draft.isSystem ? 'Edit system field' : isNew ? 'Add field' : 'Edit field'}</DialogTitle>
          <DialogDescription>
            {isNew ? 'Add a field to this request form.' : 'Change this field. Save is available once something has changed.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Basics */}
          <div className="space-y-2">
            <Label htmlFor="rtf-name">Field name <span className="text-red-500">*</span></Label>
            <Input
              id="rtf-name"
              value={draft.name}
              onChange={(e) => update({ name: e.target.value })}
              placeholder="e.g. Department"
              disabled={draft.isSystem}
              aria-invalid={duplicate || undefined}
            />
            {duplicate && <p className="text-xs text-red-600" role="alert">A field with this name already exists on this form.</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="rtf-portal-label">Portal label (optional)</Label>
            <Input
              id="rtf-portal-label"
              value={draft.portalLabel || ''}
              onChange={(e) => update({ portalLabel: e.target.value })}
              placeholder="e.g. Which department do you work in?"
            />
            <p className="text-xs text-muted-foreground">What customers see on the portal, when it should differ from the field name above.</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="rtf-type">Field type <span className="text-red-500">*</span></Label>
            <Select value={type} onValueChange={(v) => update({ type: v })} disabled={draft.isSystem}>
              <SelectTrigger id="rtf-type" aria-label="Field type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TYPE_GROUPS.map((group) => (
                  <SelectGroup key={group}>
                    <SelectLabel>{group}</SelectLabel>
                    {FIELD_TYPES.filter((t) => t.group === group).map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <Switch id="rtf-required" checked={draft.required} onCheckedChange={(c) => update({ required: c })} disabled={draft.isSystem} />
            <Label htmlFor="rtf-required">Required</Label>
          </div>

          <div className="space-y-2">
            <Label htmlFor="rtf-help">Help text (optional)</Label>
            <Textarea
              id="rtf-help"
              value={draft.helpText || ''}
              onChange={(e) => update({ helpText: e.target.value })}
              placeholder="A short explanation shown under the field"
              rows={2}
            />
          </div>

          {/* Options */}
          {withOptions && (
            <div className="space-y-2 rounded-lg border p-4" data-testid="rtf-options">
              <Label htmlFor="rtf-options-raw">{isAsset ? 'Asset types' : 'Options'} <span className="text-red-500">*</span></Label>
              <p className="text-xs text-muted-foreground">One per line, comma-separated, or tab-separated (paste straight from Excel).</p>
              <Textarea
                id="rtf-options-raw"
                value={optionsRaw}
                onChange={(e) => applyOptions(e.target.value)}
                placeholder={isAsset ? 'Laptop, Monitor, Mobile phone' : 'IT, HR, Finance, Marketing'}
                rows={4}
              />
              {(draft.options || []).length > 0 && (
                <div className="space-y-2" data-testid="rtf-options-preview">
                  <p className="text-xs text-muted-foreground">{draft.options!.length} {isAsset ? 'asset type' : 'option'}{draft.options!.length === 1 ? '' : 's'}</p>
                  <div className="flex flex-wrap gap-1">
                    {draft.options!.map((o, i) => <Badge key={`${o}-${i}`} variant="secondary">{o}</Badge>)}
                  </div>
                </div>
              )}
              {hasOtherOption(draft.options) && (
                <p className="text-xs text-muted-foreground rounded bg-muted p-2" data-testid="rtf-other-note">
                  A short text field appears automatically when "Other" is chosen on the form.
                </p>
              )}
              {isAsset && (
                <Alert className="bg-amber-50 border-amber-200">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="text-amber-800 text-xs" data-testid="rtf-asset-review">
                    Assets fields are flagged for consultant review: they need JSM Assets, an object schema and a Jira custom field.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}

          {/* Cascading */}
          {isCascading && (
            <div className="space-y-3 rounded-lg border p-4" data-testid="rtf-cascading">
              <div className="space-y-2">
                <Label htmlFor="rtf-parents">Parent options <span className="text-red-500">*</span></Label>
                <p className="text-xs text-muted-foreground">One per line, comma-separated, or tab-separated. Each parent then gets its own child list.</p>
                <Textarea
                  id="rtf-parents"
                  value={parentsRaw}
                  onChange={(e) => applyParents(e.target.value)}
                  placeholder="Hardware, Software, Access"
                  rows={3}
                />
              </div>
              {parents.map((p) => (
                <div key={p} className="space-y-1 pl-3 border-l-2 border-slate-200">
                  <Label htmlFor={`rtf-children-${p}`}>{p} → child options</Label>
                  <Textarea
                    id={`rtf-children-${p}`}
                    value={childrenRaw[p] || ''}
                    onChange={(e) => applyChildren(p, e.target.value)}
                    placeholder="Laptop, Desktop, Monitor"
                    rows={2}
                  />
                  {parseOptionList(childrenRaw[p] || '').length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {parseOptionList(childrenRaw[p] || '').map((c, i) => <Badge key={`${c}-${i}`} variant="outline">{c}</Badge>)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Default value */}
          {type !== 'attachment' && (
            <div className="space-y-2">
              <Label htmlFor="rtf-default">Default value (optional)</Label>
              <Input
                id="rtf-default"
                value={draft.defaultValue || ''}
                onChange={(e) => update({ defaultValue: e.target.value })}
                placeholder={withOptions || isCascading ? 'One of the options above' : 'Pre-filled when the form opens'}
              />
            </div>
          )}

          {/* Show/hide condition */}
          <div className="space-y-3 rounded-lg border p-4">
            <div className="flex items-center gap-2">
              <Switch id="rtf-condition" checked={showCondition} onCheckedChange={setCondition} disabled={otherFields.length === 0} />
              <Label htmlFor="rtf-condition" className="flex items-center gap-2"><Eye className="h-4 w-4" /> Show this field only when another field has a value</Label>
            </div>
            {otherFields.length === 0 && <p className="text-xs text-muted-foreground">Add another field first; a condition compares against it.</p>}
            {showCondition && (
              <div className="space-y-3 pl-1" data-testid="rtf-condition-editor">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label>Field</Label>
                    <Select value={draft.conditionalParent || ''} onValueChange={(v) => update({ conditionalParent: v, conditionalValue: '' })}>
                      <SelectTrigger aria-label="Condition field"><SelectValue placeholder="Choose a field" /></SelectTrigger>
                      <SelectContent>
                        {otherFields.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label>Is</Label>
                    <Select value={draft.conditionalOperator || 'equals'} onValueChange={(v) => update({ conditionalOperator: v as ConditionOperator })}>
                      <SelectTrigger aria-label="Condition operator"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CONDITION_OPERATORS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="rtf-condition-value">Value</Label>
                    {parentChoices.length > 0 && draft.conditionalOperator !== 'contains' ? (
                      <Select value={draft.conditionalValue || ''} onValueChange={(v) => update({ conditionalValue: v })}>
                        <SelectTrigger id="rtf-condition-value" aria-label="Condition value"><SelectValue placeholder="Choose a value" /></SelectTrigger>
                        <SelectContent>
                          {parentChoices.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input id="rtf-condition-value" value={draft.conditionalValue || ''} onChange={(e) => update({ conditionalValue: e.target.value })} placeholder="Enter a value" />
                    )}
                  </div>
                </div>
                <Alert className="bg-yellow-50 border-yellow-200">
                  <Info className="h-4 w-4 text-yellow-600" />
                  <AlertDescription className="text-yellow-900 text-xs">
                    <strong>Need more than one condition?</strong> Several conditions add real complexity in JSM and may be out of scope. Describe them in the client requirement notes instead.
                  </AlertDescription>
                </Alert>
              </div>
            )}
          </div>

          {/* Hidden from the portal */}
          <div className="space-y-3 rounded-lg border p-4">
            <div className="flex items-center gap-2">
              <Switch id="rtf-hidden" checked={!!draft.hideAndPrepopulate} onCheckedChange={(c) => update({ hideAndPrepopulate: c, prepopulatedValue: c ? draft.prepopulatedValue : undefined })} />
              <Label htmlFor="rtf-hidden" className="flex items-center gap-2"><EyeOff className="h-4 w-4" /> Hide from the portal and fill with a fixed value</Label>
            </div>
            {draft.hideAndPrepopulate && (
              <div className="space-y-2 pl-1" data-testid="rtf-hidden-editor">
                <Label htmlFor="rtf-hidden-value">Fixed value <span className="text-red-500">*</span></Label>
                <Input id="rtf-hidden-value" value={draft.prepopulatedValue || ''} onChange={(e) => update({ prepopulatedValue: e.target.value })} placeholder="The value every request gets" />
                <p className="text-xs text-muted-foreground">
                  For values that come from the user's directory record or an asset, do not hide the field here: leave it off the portal form and fill it with an automation after the request is created.
                </p>
              </div>
            )}
          </div>

          {/* Reporting and automation */}
          <div className="space-y-3 rounded-lg border p-4">
            <div className="flex items-center gap-2">
              <Checkbox id="rtf-reporting" checked={!!draft.usedForReporting} onCheckedChange={(c) => update({ usedForReporting: c === true })} />
              <Label htmlFor="rtf-reporting">Used for reporting</Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="rtf-automation" checked={!!draft.usedForAutomation} onCheckedChange={(c) => update({ usedForAutomation: c === true })} />
              <Label htmlFor="rtf-automation">Used in automation</Label>
            </div>
            {draft.usedForAutomation && (
              <div className="space-y-1 pl-6">
                <Label htmlFor="rtf-automation-use">How is it used? <span className="text-red-500">*</span></Label>
                <Textarea id="rtf-automation-use" value={draft.automationUseCase || ''} onChange={(e) => update({ automationUseCase: e.target.value })} placeholder="e.g. Routes the request to the right team by department" rows={2} />
              </div>
            )}
            {customField && (
              <p className="text-xs text-slate-700 flex items-center gap-2 rounded bg-slate-100 p-2" data-testid="rtf-custom-field">
                <Wrench className="h-3.5 w-3.5" /> Needs a Jira custom field: fields used for reporting or automation, hidden with a fixed value, or holding Assets objects cannot use a built-in field.
              </p>
            )}
          </div>

          {/* Automation triggers */}
          {withTriggers && (
            <div className="space-y-3 rounded-lg border p-4" data-testid="rtf-triggers">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2"><Zap className="h-4 w-4" /> Automation triggers</Label>
                <Button type="button" variant="outline" size="sm" onClick={addTrigger} disabled={triggerChoices.length === 0}>
                  <Plus className="h-4 w-4 mr-1" /> Add trigger
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">When a value is chosen, something should happen. Add one per value; several per field are fine.</p>
              {triggerChoices.length === 0 && <p className="text-xs text-muted-foreground">Add options above first.</p>}
              {triggers.map((t) => (
                <div key={t.id} className="rounded border p-3 space-y-2 bg-muted/30">
                  <div className="flex items-start gap-2">
                    <div className="flex-1 space-y-2">
                      <div className="space-y-1">
                        <Label>When this value is chosen</Label>
                        <Select value={t.triggerValue} onValueChange={(v) => updateTrigger(t.id, { triggerValue: v })}>
                          <SelectTrigger aria-label="Trigger value"><SelectValue placeholder="Choose a value" /></SelectTrigger>
                          <SelectContent>
                            {triggerChoices.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor={`rtf-trigger-desc-${t.id}`}>What should happen</Label>
                        <Textarea id={`rtf-trigger-desc-${t.id}`} value={t.automationDescription || ''} onChange={(e) => updateTrigger(t.id, { automationDescription: e.target.value })} placeholder="e.g. Notify the manager; create a linked Jira ticket" rows={2} />
                      </div>
                    </div>
                    <Button type="button" variant="ghost" size="icon" aria-label="Remove trigger" onClick={() => removeTrigger(t.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {triedToSave && errors.length > 0 && (
            <Alert variant="destructive" role="alert" data-testid="rtf-errors">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <ul className="list-disc pl-4 space-y-0.5 text-xs">
                  {errors.map((e) => <li key={e}>{e}</li>)}
                </ul>
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={!canSave} data-testid="rtf-save">
            {isNew ? 'Add field' : 'Save field'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
